import React, { useState } from "react";
import { GripVertical, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { CotCatalogSearch } from "@/pages/cotizador/CotCatalogSearch";
import { CotSalesDescription, salesFromPlantilla } from "@/pages/cotizador/CotSalesDescription";
import {
  COT_SERVICE_TYPE_LABELS, MODALITY_LABELS, effQty, fmtEUR, mapProductType, serviceTypeToProductType,
} from "@/pages/cotizador/shared";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const NumInput = (props) => (
  <input {...props} className={`h-7 rounded border bg-background px-1.5 text-right text-xs tabular-nums outline-none focus:ring-1 focus:ring-ring ${props.className || ""}`} />
);
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </label>
);

export function CotServiceRow({ service, dayCity, numTravelers, lineResult, onChange, onRemove, onDragStart }) {
  const s = service;
  const isLodging = s.type === "alojamiento";
  const isFree = s.type === "free_day";
  // Coche de alquiler: se factura por coche (capacidad 4-5 pax). La cantidad la fija el
  // agente manualmente y NO escala con el nº de viajeros ni cuenta como "precio por pax".
  const isRentalCar = s.type === "rental_car";
  // Trenes y vuelos son transporte inter-ciudad: no se filtran por la ciudad del día.
  const isTransport = s.type === "tren" || s.type === "vuelo";
  const [savingCat, setSavingCat] = useState(false);
  const set = (patch) => onChange(patch);

  const handlePick = (p) => {
    const isKK = p.source === "kimkim";
    const isKKLodging = p.source === "kimkim_lodging";
    const type = mapProductType(p.type, p.name);
    const lodging = type === "alojamiento";
    const pax = Math.max(1, parseInt(p.pax || 1, 10));
    const totalIncl = Number(p.cost_tax_incl) || Number(p.cost_tax_excl) || 0;
    const totalExcl = totalIncl;

    // Base común
    const patch = {
      product_id: (isKK || isKKLodging) ? null : p.id,
      name: p.name,
      type,
      provider_id: p.provider_id || null,
      provider_name: p.provider_name || "",
      city: p.city || "",
      country: p.country || "",
      currency: p.currency || "EUR",
      cost_tax_incl: lodging ? totalIncl : round2(totalIncl / pax),
      cost_tax_excl: lodging ? totalExcl : round2(totalExcl / pax),
      pax: lodging ? 1 : pax,
      qty: (lodging || type === "rental_car") ? (s.qty || 1) : (numTravelers || s.qty || 1),
      // Punto 8: duración y modalidad deducidas del catálogo (el consultor puede corregirlas).
      duration: p.duration || s.duration || "",
      modality: p.modality || s.modality || "",
    };

    if (isKK) {
      patch.kimkim_addon_id = p.kimkim_addon_id;
      patch.kk_price_gross = p.kk_price_gross;
      patch.kk_price_kind = p.kk_price_kind;
      patch.kk_price_source = p.kk_price_source;
      patch.kk_pricing_display = p.kk_pricing_display;
      patch.kk_pricing_table_row = p.kk_pricing_table_row;
      patch.kk_pax_covered = p.pax;
      patch.kimkim_lodging_id = null;
      // Descripción de venta: cargar desde KimKim si el campo está vacío (respetamos ediciones del agente).
      if (!s.sales_description && p.description) {
        patch.sales_description = p.description;
      }
      if ((!s.sales_images || s.sales_images.length === 0) && p.main_image) {
        patch.sales_images = [p.main_image];
      }
    } else if (isKKLodging) {
      patch.kimkim_lodging_id = p.kimkim_lodging_id;
      patch.lodging_stars = p.stars || null;
      patch.lodging_level = p.level || null;
      patch.lodging_is_apartment = !!p.is_apartment;
      patch.lodging_reservation_via = p.reservation_via || null;
      patch.lodging_notes = p.notes_excel || null;
      patch.lodging_quality_notes = p.quality_notes || null;
      patch.lodging_main_image = p.main_image || null;
      patch.lodging_description = p.description || null;
      patch.kimkim_addon_id = null;
      patch.kk_price_gross = null;
      // Descripción del hotel: cargar la de KimKim si el campo de venta está vacío.
      if (!s.sales_description && p.description) {
        patch.sales_description = p.description;
      }
      if ((!s.sales_images || s.sales_images.length === 0) && (p.main_image || (p.images && p.images.length))) {
        patch.sales_images = p.images && p.images.length ? p.images.slice(0, 4) : [p.main_image];
      }
    } else {
      patch.kimkim_addon_id = null;
      patch.kimkim_lodging_id = null;
      patch.kk_price_gross = null;
    }
    set(patch);

    // Autorrellena descripción de venta desde plantilla TP vinculada (solo aplica al legacy).
    if (!isKK && !isKKLodging && p.tp_plantilla_id && !s.plantilla_id && !s.sales_description) {
      salesFromPlantilla(p.tp_plantilla_id).then((patchTP) => set(patchTP)).catch(() => {});
    }
  };

  // Guardar la tarifa corregida en el catálogo (PUT si ya existe; POST si es manual).
  const saveToCatalog = async () => {
    if (!s.name?.trim()) { toast.error("Falta el nombre del servicio"); return; }
    setSavingCat(true);
    try {
      const pax = Math.max(1, parseInt(s.pax || 1, 10));
      const perIncl = Number(s.cost_tax_incl) || Number(s.cost_tax_excl) || 0;
      const perExcl = perIncl;
      // El catálogo guarda el coste TOTAL para `pax` personas (igual que en el legacy).
      const payload = {
        name: s.name,
        type: serviceTypeToProductType(s.type) || "actividad",
        city: s.city || "", country: s.country || "",
        cost_tax_excl: isLodging ? perExcl : round2(perExcl * pax),
        cost_tax_incl: isLodging ? perIncl : round2(perIncl * pax),
        currency: s.currency || "EUR",
        pax: isLodging ? null : pax,
        provider_id: s.provider_id || null,
        provider_name: s.provider_name || "Manual",
      };
      if (s.product_id) {
        await api.put(`/products/${s.product_id}`, payload);
        toast.success("Tarifa actualizada en el catálogo");
      } else {
        const { data } = await api.post("/products", payload);
        set({ product_id: data.id });
        toast.success("Añadida al catálogo");
      }
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSavingCat(false); }
  };

  return (
    <div className="rounded-md border bg-card p-2.5" data-testid={`cot-service-${s.id}`}>
      <div className="flex items-start gap-2">
        <span draggable onDragStart={onDragStart} className="mt-1 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing" title="Arrastra para reordenar o mover de día" data-testid={`svc-drag-${s.id}`}><GripVertical className="h-4 w-4" /></span>
        <Select value={s.type} onValueChange={(v) => set({ type: v })}>
          <SelectTrigger className="h-8 w-40 shrink-0 text-xs" data-testid={`svc-type-${s.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(COT_SERVICE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <div className="min-w-0 flex-1">
          <CotCatalogSearch
            value={s.name} city={isLodging || isTransport ? "" : dayCity} productType={serviceTypeToProductType(s.type)} pax={numTravelers}
            testid={`svc-name-${s.id}`} placeholder={isFree ? "Día libre (descripción)" : (isLodging ? "Buscar hotel del catálogo…" : "Servicio · catálogo o manual")}
            onTextChange={(v) => set({ name: v })} onPick={handlePick}
          />
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            {(s.provider_name || s.city || s.country) && <span className="truncate">{[s.provider_name, s.city, s.country].filter(Boolean).join(" · ")}</span>}
            {!isFree && !isLodging && !isRentalCar && (
              <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-foreground" title="Para cuántos pax cuenta el precio unitario. 1 = precio por persona. Súbelo si el proveedor cotiza por grupo." data-testid={`svc-pax-badge-${s.id}`}>
                precio para
                <input type="number" min="1" max="50" value={s.pax ?? 1} onChange={(e) => set({ pax: Math.max(1, parseInt(e.target.value || "1", 10)) })} onClick={(e) => e.stopPropagation()} className="w-9 rounded border bg-background px-1 text-center outline-none" data-testid={`svc-pax-${s.id}`} />
                pax
              </span>
            )}
            {s.product_id && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-emerald-700">catálogo</span>}
            {s.kimkim_addon_id && (
              <span
                className="rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-blue-700"
                title={`Addon KimKim #${s.kimkim_addon_id} · Precio KK original: ${s.kk_price_gross ?? "?"} ${s.currency || "EUR"} (con 8 % comisión). Neto aplicado: ${s.cost_tax_incl ?? "?"} ${s.currency || "EUR"}/pax.`}
                data-testid={`svc-kk-badge-${s.id}`}
              >
                KK {Number(s.kk_price_gross || 0).toFixed(0)} {s.currency === "USD" ? "$" : "€"}
              </span>
            )}
          </div>
        </div>
        {!isFree && (
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary" onClick={saveToCatalog} disabled={savingCat} title={s.product_id ? "Actualizar esta tarifa en el catálogo" : "Guardar este servicio en el catálogo"} data-testid={`svc-save-catalog-${s.id}`}>
            <Save className="h-4 w-4" />
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={onRemove} data-testid={`svc-remove-${s.id}`} aria-label="Quitar servicio"><Trash2 className="h-4 w-4" /></Button>
      </div>

      {!isFree && (
        <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-2">
          {isLodging ? (
            <>
              <Field label="Noches"><NumInput data-testid={`svc-nights-${s.id}`} type="number" min="1" value={s.nights ?? 1} onChange={(e) => set({ nights: parseInt(e.target.value || "1", 10) })} className="w-16" /></Field>
              <Field label="Habs."><NumInput data-testid={`svc-rooms-${s.id}`} type="number" min="1" value={s.rooms ?? 1} onChange={(e) => set({ rooms: parseInt(e.target.value || "1", 10) })} className="w-16" /></Field>
            </>
          ) : (
            <Field label={isRentalCar ? "Cant. (coches)" : "Cant. (pax)"}><NumInput data-testid={`svc-qty-${s.id}`} type="number" min="0" step="1" value={s.qty ?? 1} onChange={(e) => set({ qty: parseFloat(e.target.value || "0") })} className="w-16" /></Field>
          )}
          <Field label="País"><input data-testid={`svc-country-${s.id}`} value={s.country || ""} onChange={(e) => set({ country: e.target.value })} className="h-7 w-28 rounded border bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring" placeholder="Spain…" /></Field>
          <Field label={isLodging ? "Coste noche (IVA incl.)" : "Coste p/pax (IVA incl.)"}><NumInput data-testid={`svc-cost-incl-${s.id}`} type="number" min="0" step="0.01" value={(Number(s.cost_tax_incl) || Number(s.cost_tax_excl) || 0)} onChange={(e) => { const v = parseFloat(e.target.value || "0"); set({ cost_tax_incl: v, cost_tax_excl: v }); }} className="w-24" /></Field>
          {!isLodging && !isRentalCar && (
            <>
              <Field label="Duración"><input data-testid={`svc-duration-${s.id}`} value={s.duration || ""} onChange={(e) => set({ duration: e.target.value })} placeholder="3 hours" className="h-7 w-24 rounded border bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring" /></Field>
              <Field label="Modalidad">
                <Select value={s.modality || "none"} onValueChange={(v) => set({ modality: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid={`svc-modality-${s.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— sin dato —</SelectItem>
                    {Object.entries(MODALITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}
          <Field label="Inclusión">
            <Select value={s.option_type || "included"} onValueChange={(v) => set({ option_type: v })}>
              <SelectTrigger className="h-7 w-32 text-xs" data-testid={`svc-opttype-${s.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="included">Incluido</SelectItem>
                <SelectItem value="optional">Opcional</SelectItem>
                <SelectItem value="alternative">Alternativa</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              PVP línea (×{effQty(s)})
              {s.option_type && s.option_type !== "included" && (
                <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-700">no suma</span>
              )}
            </div>
            <div className={`font-semibold tabular-nums ${s.option_type && s.option_type !== "included" ? "text-muted-foreground line-through" : ""}`} data-testid={`svc-line-pvp-${s.id}`}>{fmtEUR(lineResult?.line_quoted || 0)}</div>
          </div>
        </div>
      )}

      <CotSalesDescription
        name={s.name}
        plantillaId={s.plantilla_id}
        description={s.sales_description}
        images={s.sales_images}
        category={s.type}
        city={s.city || dayCity}
        onChange={(patch) => set(patch)}
        testid={`svc-sales-${s.id}`}
      />
    </div>
  );
}
